import PropTypes from 'prop-types'
import { createSelector } from 'reselect'

import {
  DefaultTopicMetricsState
} from '../reducers/topicMetrics_reducer'

// -----------------------------------------------------------------------------

// Topic Top Level Metrics Selectors

// -----------------------------------------------------------------------------

/* Fetches the top level metrics by id, assuming an id is in the url */
const getTopicMetrics = (state, props) => {
  // If we got here via a shortcut, use it to get underlying topic id
  let topicId = null
  // We used to have a check here that target was 'Entity', but we're removing that now that we
  // store most everything in the topic.byId state. This should get moved too.
  if (props.shortcut && props.shortcut.target_id) {
    topicId = props.shortcut.target_id
  } else {
    topicId = props.match.params.topicId
  }
  return (state.topics.byId[topicId] || {}).metrics
}

/* Smartly returns the metrics or the default state */
const getTopicMetricsState = createSelector(
  [getTopicMetrics],
  (metrics) => {
    return metrics || DefaultTopicMetricsState
  }
)

const getTopicsById = (state) => {
  return state.topics.byId
}

const TopicMetricPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  leftValue: PropTypes.number,
  rightValue: PropTypes.number
})

const TopicMetricsPropType = PropTypes.arrayOf(TopicMetricPropType)

const AllTopicMetricsPropType = PropTypes.shape({
  vpp: TopicMetricsPropType.isRequired,
  network: TopicMetricsPropType.isRequired,
  streaming: TopicMetricsPropType.isRequired
})

const makeMetrics = (metrics) => {
  let data = null
  let networkPreferance = null
  let brandPreferance = null
  let genrePreferance = null
  let streamingPreferance = null
  if (metrics.details && metrics.details.metrics) {
    const make = (name, optionalBucketName) => {
      const bucketName = optionalBucketName || name
      const bucket = metrics.details.metrics.find(m => m.bucket_name === bucketName)
      const score = (bucket || {}).score || 0
      const value = score
      return {
        name,
        value
      }
    }
    const makePreferenceList = () => {
      //Array on networks we do not want to temporarily display
      const toRemove = []
      const updatedNetworkPreferenceList = metrics.details.network_labels.filter(( el ) => !toRemove.includes( el ))
      let networkData = updatedNetworkPreferenceList.map(type => {
         return make(type)
      })
      networkPreferance = networkData.sort((a, b) => b.value - a.value)

      if (metrics.details.derived_brand_labels) {
        let brandData = metrics.details.derived_brand_labels.map(type => {
          return make(type)
        })
        brandPreferance = brandData.sort((a, b) => b.value - a.value)
      }
      if (metrics.details.genre_labels) {
        let genreData = metrics.details.genre_labels.map(type => {
          return make(type)
        })
        genrePreferance = genreData.sort((a, b) => b.value - a.value)
      }
      if (metrics.details.streaming_labels) {
        let streamingData = metrics.details.streaming_labels.map(type => {
          return make(type)
        })
        streamingPreferance = streamingData.sort((a, b) => b.value - a.value)
      }
    }
    makePreferenceList()
    data = {
      vpp: [
        make('Network', 'Linear'),
        make('Streaming'),
        make('Online Video', 'AllVideo'),
        make('Gaming', 'EntityType.Gaming')
      ],
      vppBrand: [
        make('Brands', 'EntityType.Brand')
      ],
      broadcast: [
        make('ABC'),
        make('CBS'),
        make('The CW'),
        make('FOX'),
        make('NBC')
      ],
      streaming: streamingPreferance,
      entity: [
        make('Shows', 'EntityType.Show'),
        make('Movies', 'EntityType.Movie'),
        make('Music', 'EntityType.Music'),
        make('People', 'EntityType.People')
      ],
      channel: networkPreferance,
      genre: genrePreferance,
      brand: brandPreferance
    }
  }
  return {
    isFetching: metrics.isFetching || metrics.didInvalidate || !metrics.details,
    error: metrics.error,
    details: data
  }
}

const makeCompareMetrics = (leftMetrics, rightMetrics) => {
  let data = null
  let top5NetworkPreferance = null
  let bottom5NetworkPreferance = null
  let top5BrandPreferance = []
  let bottom5BrandPreferance = []
  if (leftMetrics.details && leftMetrics.details.metrics && rightMetrics.details && rightMetrics.details.metrics) {
    const make = (name, optionalBucketName) => {
      const bucketName = optionalBucketName || name
      const leftBucket = leftMetrics.details.metrics.find(m => m.bucket_name === bucketName)
      const rightBucket = rightMetrics.details.metrics.find(m => m.bucket_name === bucketName)
      const leftScore = (leftBucket || {}).score || 0
      const rightScore = (rightBucket || {}).score || 0
      return {
        name,
        leftValue: leftScore,
        rightValue: rightScore
      }
    }

    const makePreferenceList = () => {
      let networkData = leftMetrics.details.network_labels.map(type => {
        return make(type)
      })
      let sortedNetworkData = networkData.sort((a, b) => b.leftValue - a.leftValue)
      top5NetworkPreferance = sortedNetworkData.slice(0, 5)
      bottom5NetworkPreferance = sortedNetworkData.slice(-5).sort((a, b) => a.leftValue - b.leftValue)

      if (leftMetrics.details.derived_brand_labels) {
        let brandData = leftMetrics.details.derived_brand_labels.map(type => {
          return make(type)
        })
        let sortedBrandData = brandData.sort((a, b) => b.leftValue - a.leftValue)
        top5BrandPreferance = sortedBrandData.slice(0, 5)
        bottom5BrandPreferance = sortedBrandData.slice(-5)
      }
    }

    makePreferenceList()

    data = {
      vpp: [
        make('Network', 'Linear'),
        make('Streaming'),
        make('Online Video', 'AllVideo'),
        make('Gaming', 'EntityType.Gaming')
      ],
      broadcast: [
        make('ABC'),
        make('CBS'),
        make('The CW'),
        make('FOX'),
        make('NBC')
      ],
      streaming: [
        make('Amazon'),
        make('Hulu'),
        make('Netflix'),
        make('Disney+'),
        make('Apple TV+')
      ],
      entity: [
        make('Show', 'EntityType.Show'),
        make('Movie', 'EntityType.Movie'),
        make('Music', 'EntityType.Music'),
        make('Person', 'EntityType.People'),
        make('Brand', 'EntityType.Brand')
      ],
      top5NP: top5NetworkPreferance,
      bottom5NP: bottom5NetworkPreferance,
      top5BP: top5BrandPreferance,
      bottom5BP: bottom5BrandPreferance
    }
  }
  return {
    isFetching: leftMetrics.isFetching || leftMetrics.didInvalidate || !leftMetrics.details,
    error: leftMetrics.error,
    details: data
  }
}

const getCompareTopicIds = (state, props) => {
  let compareTopicIds = []
  if (props.shortcut && props.shortcut.left_target_id && props.shortcut.right_target_id) {
    compareTopicIds = [props.shortcut.left_target_id, props.shortcut.right_target_id]
  } else {
    compareTopicIds = [props.match.params.id1, props.match.params.id2]
  }
  return compareTopicIds
}

/* Creates a selector that converts raw metric api data into a form more friendly for the UI */
const makeTopicMetricsSelector = () => {
  return createSelector(
    [getTopicMetricsState],
    (metrics) => {
      return makeMetrics(metrics)
    }
  )
}

const makePlanTopLevelMetricsSelector = () => {
  return createSelector(
    [getCompareTopicIds, getTopicsById],
    (topicIds, topicsById) => {
      const planTopLevelMetric = makeCompareMetrics(((topicsById[topicIds[0]] || {}).metrics || DefaultTopicMetricsState), ((topicsById[topicIds[1]] || {}).metrics || DefaultTopicMetricsState))
      return planTopLevelMetric
    }
  )
}

// -----------------------------------------------------------------------------

// Exports

// -----------------------------------------------------------------------------

export {
  makeTopicMetricsSelector,
  TopicMetricsPropType,
  AllTopicMetricsPropType,
  makePlanTopLevelMetricsSelector
}
